// The best order for a run's stops: the loop from its start, through every
// stop still to deliver, and back, that Google Maps finds shortest to drive
// (Directions with optimizeWaypoints, from the Maps library the app already
// loads). Create run sorts with it, the run sheet's Best order saves it, and
// Add to run sorts the run again after adding orders.
//
// Google sorts at most MAX_WAYPOINTS stops in one request. A longer run is
// sorted in steps (bestLongOrder): where each stop is, from the drive in its
// current order; the shortest loop on straight lines; then groups of
// MAX_WAYPOINTS stops in a row sorted by Google on real roads, each group's
// two ends kept in place, the groups overlapping so stops can move between
// them. The new order is kept only when it drives quicker.

import { reorderRunStops } from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import { deliveryAddress } from "./routeSheet";
import {
  askDirections,
  legOf,
  legTotals,
  loopPoints,
  MAX_WAYPOINTS,
  routeLegs,
  straightMeters,
} from "./runDistance";
import { RUN_MAX_STOPS } from "./runs";

// Why stops couldn't be sorted, as the pop-ups say it.
export const SORT_PROBLEMS = {
  start: "Sorting needs a start location with an address",
  "too-many": `Runs of more than ${RUN_MAX_STOPS} stops aren't sorted`,
  google: "Google Maps couldn't work out the best order",
  save: "Couldn't save the new order",
};

const cache = new Map();

// One optimized Directions request: { order, meters, seconds, legs } or null.
// `order` lists the addresses' indexes in the order to drive them; `legs`
// the drive's legs in that order.
const askGoogle = async (maps, origin, destination, addresses) => {
  const { result, status } = await askDirections(maps, {
    origin,
    destination,
    waypoints: addresses.map((location) => ({ location, stopover: true })),
    optimizeWaypoints: true,
    travelMode: "DRIVING",
  });
  const route = result?.routes?.[0];
  const order = route?.waypoint_order;
  if (
    status !== "OK" ||
    !Array.isArray(order) ||
    order.length !== addresses.length
  ) {
    return null;
  }
  const legs = (route?.legs ?? []).map(legOf);
  return { order, legs, ...legTotals(legs) };
};

// The same request is asked once per page load; a failed one is asked again.
const bestRoute = (maps, origin, destination, addresses) => {
  const key = [origin, destination, ...addresses].join("|");
  if (!cache.has(key)) {
    cache.set(
      key,
      askGoogle(maps, origin, destination, addresses).then((found) => {
        if (!found) cache.delete(key);
        return found;
      }),
    );
  }
  return cache.get(key);
};

// A new order is kept when it's quicker (a minute or more), or about as
// quick and shorter (0.5 km or more), as the run sheet's Best order does.
export const isQuicker = (now, was) =>
  now.seconds <= was.seconds - 60 ||
  (now.seconds < was.seconds + 60 && now.meters <= was.meters - 500);

const sameOrder = (a, b) =>
  a.length === b.length && a.every((value, at) => value === b[at]);

// --- Straight lines: a path from `first` through `inner` to `last` (node
// numbers into the distance table D).

const pathMeters = (D, first, inner, last) => {
  let meters = 0;
  let at = first;
  inner.forEach((node) => {
    meters += D[at][node];
    at = node;
  });
  return meters + D[at][last];
};

// Shorter until it can't be: reversing a stretch (2-opt), and moving 1 to 3
// stops in a row elsewhere, either way round.
const localSearch = (D, first, inner, last) => {
  let path = [first, ...inner, last];
  let better = true;
  for (let round = 0; better && round < 200; round += 1) {
    better = false;
    for (let i = 0; i < path.length - 2; i += 1) {
      for (let k = i + 1; k < path.length - 1; k += 1) {
        const change =
          D[path[i]][path[k]] +
          D[path[i + 1]][path[k + 1]] -
          D[path[i]][path[i + 1]] -
          D[path[k]][path[k + 1]];
        if (change < -1e-6) {
          path = [
            ...path.slice(0, i + 1),
            ...path.slice(i + 1, k + 1).reverse(),
            ...path.slice(k + 1),
          ];
          better = true;
        }
      }
    }
    for (let size = 1; size <= 3; size += 1) {
      for (let i = 1; i + size < path.length; i += 1) {
        const run = path.slice(i, i + size);
        const rest = [...path.slice(0, i), ...path.slice(i + size)];
        const saved =
          D[path[i - 1]][path[i]] +
          D[path[i + size - 1]][path[i + size]] -
          D[path[i - 1]][path[i + size]];
        const ways = size > 1 ? [run, [...run].reverse()] : [run];
        let best = null;
        for (let j = 0; j < rest.length - 1; j += 1) {
          for (const way of ways) {
            const change =
              D[rest[j]][way[0]] +
              D[way[way.length - 1]][rest[j + 1]] -
              D[rest[j]][rest[j + 1]] -
              saved;
            if (change < -1e-6 && (!best || change < best.change)) {
              best = { change, j, way };
            }
          }
        }
        if (best) {
          path = [
            ...rest.slice(0, best.j + 1),
            ...best.way,
            ...rest.slice(best.j + 1),
          ];
          better = true;
        }
      }
    }
  }
  return path.slice(1, -1);
};

// Always the next closest stop.
const nearestNeighbour = (D, first, nodes) => {
  const left = new Set(nodes);
  const path = [];
  let at = first;
  while (left.size) {
    let next = null;
    for (const node of left) {
      if (next === null || D[at][node] < D[at][next]) next = node;
    }
    path.push(next);
    left.delete(next);
    at = next;
  }
  return path;
};

// Repeatable random numbers (mulberry32), so a run sorts the same each time.
const randomFrom = (seed) => {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const SEARCH_KICKS = 200;
const SEARCH_MS = 250;
const pause = () => new Promise((resolve) => setTimeout(resolve, 0));

// Local search, then shaken up (three stretches swapped) and searched again
// while it keeps getting shorter, for at most SEARCH_KICKS shakes or
// SEARCH_MS; the page gets a turn every 20 shakes.
const iteratedSearch = async (D, first, inner, last, seed) => {
  const random = randomFrom(seed);
  const began = Date.now();
  let path = localSearch(D, first, inner, last);
  let meters = pathMeters(D, first, path, last);
  for (let kick = 0; kick < SEARCH_KICKS && path.length >= 8; kick += 1) {
    if (Date.now() - began > SEARCH_MS) break;
    // eslint-disable-next-line no-await-in-loop
    if (kick % 20 === 19) await pause();
    const cuts = new Set();
    while (cuts.size < 3)
      cuts.add(1 + Math.floor(random() * (path.length - 1)));
    const [a, b, c] = [...cuts].sort((x, y) => x - y);
    const shaken = localSearch(
      D,
      first,
      [
        ...path.slice(0, a),
        ...path.slice(b, c),
        ...path.slice(a, b),
        ...path.slice(c),
      ],
      last,
    );
    const shakenMeters = pathMeters(D, first, shaken, last);
    if (shakenMeters < meters - 1e-6) {
      path = shaken;
      meters = shakenMeters;
    }
  }
  return path;
};

// --- Real roads: groups of MAX_WAYPOINTS stops in a row, sorted by Google
// with the stops before and after them fixed. A group's new order is kept
// when it drives at least WINDOW_GAIN seconds quicker. `legs[k]` is the leg
// into `order[k]`; the last one goes back to `destination`.
const WINDOW_STEP = 13;
const WINDOW_ROUNDS = 3;
const WINDOW_GAIN = 30;

const sortWindows = async (
  maps,
  origin,
  destination,
  addresses,
  order,
  legs,
) => {
  const n = order.length;
  const starts = [];
  for (let from = 0; ; from += WINDOW_STEP) {
    starts.push(from);
    if (from + MAX_WAYPOINTS >= n) break;
  }
  for (let round = 0; round < WINDOW_ROUNDS; round += 1) {
    let better = false;
    const turn = round % 2 ? [...starts].reverse() : starts;
    for (const from of turn) {
      const to = Math.min(n, from + MAX_WAYPOINTS);
      const group = order.slice(from, to);
      // eslint-disable-next-line no-await-in-loop
      const found = await bestRoute(
        maps,
        from ? addresses[order[from - 1]] : origin,
        to < n ? addresses[order[to]] : destination,
        group.map((at) => addresses[at]),
      );
      if (!found || found.legs.length !== group.length + 1) return null;
      const before = legTotals(legs.slice(from, to + 1)).seconds;
      if (found.seconds < before - WINDOW_GAIN) {
        order.splice(from, group.length, ...found.order.map((at) => group[at]));
        legs.splice(from, group.length + 1, ...found.legs);
        better = true;
      }
    }
    if (!better) break;
  }
  return { order, legs };
};

const SEEDS = [7, 11, 23];

/**
 * The quickest order found for driving from `origin` through `addresses`
 * to `destination`, for more stops than Google sorts at once. Resolves to
 * { order, meters, seconds, was } (`order`: the addresses' indexes; `was`:
 * the drive in the order given), or null when Google can't say.
 */
export const bestLongOrder = async (
  origin,
  destination,
  addresses,
  maps = window.google?.maps,
) => {
  const n = addresses.length;
  const given = addresses.map((_, at) => at);
  const givenLegs = await routeLegs([origin, ...addresses, destination], maps);
  if (!givenLegs || givenLegs.length !== n + 1) return null;
  // Node 0 is the origin, 1..n the stops, n + 1 the destination.
  const places = [
    givenLegs[0].from,
    ...givenLegs.slice(0, n).map((leg) => leg.to),
    givenLegs[n].to,
  ];
  if (places.some((place) => !place)) return null;
  const D = places.map((a) => places.map((b) => straightMeters(a, b)));
  const nodes = given.map((at) => at + 1);
  const paths = [
    localSearch(D, 0, nearestNeighbour(D, 0, nodes), n + 1),
    localSearch(D, 0, nodes, n + 1),
  ];
  for (const seed of SEEDS) {
    // eslint-disable-next-line no-await-in-loop
    paths.push(
      await iteratedSearch(D, 0, nearestNeighbour(D, 0, nodes), n + 1, seed),
    );
  }
  // Each different straight-line order, driven on real roads.
  const tried = [];
  for (const path of paths) {
    const order = path.map((node) => node - 1);
    if (!tried.some((known) => sameOrder(known.order, order))) {
      let legs = givenLegs;
      if (!sameOrder(order, given)) {
        // eslint-disable-next-line no-await-in-loop
        legs = await routeLegs(
          [origin, ...order.map((at) => addresses[at]), destination],
          maps,
        );
      }
      if (!legs) return null;
      tried.push({ order, legs });
    }
  }
  tried.sort((a, b) => legTotals(a.legs).seconds - legTotals(b.legs).seconds);
  // The two quickest, sorted on real roads; the quicker kept.
  let best = null;
  for (const start of tried.slice(0, 2)) {
    // eslint-disable-next-line no-await-in-loop
    const sorted = await sortWindows(
      maps,
      origin,
      destination,
      addresses,
      [...start.order],
      [...start.legs],
    );
    if (!sorted) return null;
    if (
      !best ||
      legTotals(sorted.legs).seconds < legTotals(best.legs).seconds
    ) {
      best = sorted;
    }
  }
  return {
    order: best.order,
    ...legTotals(best.legs),
    was: legTotals(givenLegs),
  };
};

/**
 * `stops`: [{ id, address, done }] in the order they're in now. Stops
 * already marked on the run (done) stay first, as they are; the rest are
 * sorted from the last marked stop (else the start) back to the start, and
 * stops without an address go last. Resolves to { ids, changed } (`ids` in
 * the new order) or { problem } (a key of SORT_PROBLEMS).
 */
export const bestStopOrder = async (
  start,
  stops,
  maps = window.google?.maps,
) => {
  const done = stops.filter((stop) => stop.done);
  const todo = stops.filter((stop) => !stop.done && stop.address);
  const unknown = stops.filter((stop) => !stop.done && !stop.address);
  const now = stops.map((stop) => stop.id);
  if (todo.length < 2) return { ids: now, changed: false };
  if (!start) return { problem: "start" };
  if (todo.length > RUN_MAX_STOPS) return { problem: "too-many" };
  if (!maps?.DirectionsService) return { problem: "google" };
  const lastDone = [...done].reverse().find((stop) => stop.address);
  const origin = lastDone?.address || start;
  const addresses = todo.map((stop) => stop.address);
  let order;
  if (todo.length <= MAX_WAYPOINTS) {
    const found = await bestRoute(maps, origin, start, addresses);
    if (!found) return { problem: "google" };
    order = found.order;
  } else {
    const found = await bestLongOrder(origin, start, addresses, maps);
    if (!found) return { problem: "google" };
    // Not quicker than the order given: it stays.
    if (!isQuicker(found, found.was)) return { ids: now, changed: false };
    order = found.order;
  }
  const ids = [...done, ...order.map((at) => todo[at]), ...unknown].map(
    (stop) => stop.id,
  );
  return { ids, changed: ids.some((id, at) => id !== now[at]) };
};

// Driving distance and time of the best loop from `start` through
// `addresses` and back: { meters, seconds }, or null when Google can't say
// (or for more stops than Google sorts at once).
export const bestLoopTotals = async (
  start,
  addresses,
  maps = window.google?.maps,
) => {
  const stops = addresses.filter(Boolean);
  if (
    !start ||
    !stops.length ||
    stops.length > MAX_WAYPOINTS ||
    !maps?.DirectionsService
  ) {
    return null;
  }
  const found = await bestRoute(maps, start, start, stops);
  return found ? { meters: found.meters, seconds: found.seconds } : null;
};

/**
 * The loop from `start` through `addresses` (in that order) and back, and
 * what one more stop at `address` adds to it where it fits best: { total,
 * added } ({ meters, seconds } each; `added` null when it can't be worked
 * out), or null. The best place is picked on straight lines among every
 * pair of stops in a row, and the two best are driven on real roads.
 */
export const insertionTotals = async (
  start,
  addresses,
  address,
  maps = window.google?.maps,
) => {
  const points = loopPoints(start, addresses);
  if (!start || !address || !points.length || !maps?.DirectionsService) {
    return null;
  }
  const legs = await routeLegs(points, maps);
  if (!legs) return null;
  const total = legTotals(legs);
  const own = (await routeLegs([address, start], maps))?.[0]?.from;
  const places = [legs[0]?.from, ...legs.map((leg) => leg.to)];
  if (!own || places.some((place) => !place)) return { total, added: null };
  const fits = legs
    .map((_, k) => ({
      k,
      extra:
        straightMeters(places[k], own) +
        straightMeters(own, places[k + 1]) -
        straightMeters(places[k], places[k + 1]),
    }))
    .sort((a, b) => a.extra - b.extra)
    .slice(0, 2);
  const detours = await Promise.all(
    fits.map(({ k }) => routeLegs([points[k], address, points[k + 1]], maps)),
  );
  let added = null;
  detours.forEach((detour, at) => {
    if (!detour) return;
    const { k } = fits[at];
    const now = legTotals(detour);
    const extra = {
      meters: now.meters - legs[k].meters,
      seconds: now.seconds - legs[k].seconds,
    };
    if (!added || extra.seconds < added.seconds) added = extra;
  });
  return { total, added };
};

// A run's stops as bestStopOrder takes them, in the run's order.
export const runStops = (run) =>
  [...(Array.isArray(run?.stops) ? run.stops : [])]
    .sort((a, b) => Number(a?.position) - Number(b?.position))
    .map((stop) => ({
      id: stop.order_id,
      address: deliveryAddress(stop?.order?.order_shipping_address_details),
      done: Boolean(stop.outcome) && stop.outcome !== "pending",
    }));

// The run's stops with those still to deliver the other way round (the drive
// reversed); stops already marked stay first, as Best order keeps them.
// { ids, changed }: `ids` in the new order.
export const reversedStopOrder = (run) => {
  const stops = runStops(run);
  const now = stops.map((stop) => stop.id);
  const ids = [
    ...stops.filter((stop) => stop.done),
    ...stops.filter((stop) => !stop.done).reverse(),
  ].map((stop) => stop.id);
  return { ids, changed: ids.some((id, at) => id !== now[at]) };
};

// Where the run starts and ends, from the account's locations.
export const runStartAddress = (run, locations = []) =>
  locations.find(
    (location) => String(location?.id) === String(run?.start_warehouse_id),
  )?.address || "";

/**
 * Sorts an open run's stops into the best order and saves it. Resolves to
 * { run, sorted, problem }: `run` as saved (else as it was given), `sorted`
 * when its stops are now in the best order, `problem` (a key of
 * SORT_PROBLEMS) when they couldn't be sorted.
 */
export const sortRun = async (
  run,
  locations = [],
  maps = window.google?.maps,
) => {
  const best = await bestStopOrder(
    runStartAddress(run, locations),
    runStops(run),
    maps,
  );
  if (best.problem) return { run, sorted: false, problem: best.problem };
  if (!best.changed) return { run, sorted: true };
  const response = await reorderRunStops(run.id, best.ids);
  const saved = response?.error ? null : response?.data?.result;
  return saved?.id != null
    ? { run: saved, sorted: true }
    : { run, sorted: false, problem: "save" };
};

// What the Orders page says once orders joined a run: "#12 added to <run>, in
// the best stop order", or at the end with why it couldn't be sorted.
export const addedText = (what, title, sorting) =>
  sorting?.problem
    ? `${what} added at the end of ${title}: ${SORT_PROBLEMS[sorting.problem]}`
    : `${what} added to ${title}, in the best stop order`;

// Test hook: forget the routes asked for.
export const clearBestRouteCache = () => cache.clear();
